import type {
  AccountDBSource,
  DiscountedPartPrice,
  ISO4217,
  PartNode,
  PartPrice,
  ShoppingCartItem,
} from '@partful/shared-types';
import { CartEntry } from 'src/providers/ShoppingCart';
import { ColourMode } from './productInventory';

function getTotalPrice(currentParts: PartNode[], preferredCurrency: ISO4217 = 'GBP') {
  return currentParts.reduce(
    (total, currentPart) => {
      const chosenPriceV2 = currentPart?.priceV2?.find((price) => price.currency === preferredCurrency);
      const backupPriceV2 = currentPart?.priceV2?.[0];
      let { price } = currentPart;
      if (backupPriceV2) price = backupPriceV2.amount;
      if (chosenPriceV2) price = chosenPriceV2.amount;

      if (!price || price === 'por') return total;

      return {
        currency: chosenPriceV2?.currency ?? backupPriceV2?.currency ?? preferredCurrency,
        amount: price + total.amount,
      };
    },
    { currency: preferredCurrency, amount: 0 }
  );
}

interface StructuredEvent extends Record<string, unknown> {
  event: string;
}

type UnstructuredEvent = [string, string, Record<string, unknown>];

function mapPartNodesToItems(
  currentParts: PartNode[],
  tenantName: string | undefined = undefined,
  quantity: number | undefined = undefined
) {
  return currentParts.map((part, index) => ({
    item_id: part.partNumber,
    item_name: part.name,
    index,
    item_brand: tenantName,
    price: getTotalPrice([part]).amount,
    quantity,
    traffic_type: 'internal',
  }));
}

function mapCartEntriesToItems(currentCartEntries: CartEntry[], tenantName: string | undefined = undefined) {
  return currentCartEntries.map((entry, index) => ({
    item_id: entry.partNumber,
    item_name: entry.partName,
    index,
    item_brand: tenantName,
    price: entry.unitPrice,
    quantity: entry.quantity,
    traffic_type: 'internal',
  }));
}
/* istanbul ignore next */
function constructItemEvent(tenant: AccountDBSource, currentParts: PartNode[], quantity: number = 0) {
  const items = mapPartNodesToItems(currentParts, tenant.name, quantity);
  const price = getTotalPrice(currentParts, tenant.preferredCurrency);

  return {
    currency: price.currency,
    value: items.reduce((prev, curr) => prev + curr.price, 0) * quantity,
    items,
  };
}

export interface GTMDataPurchase {
  items: ShoppingCartItem[];
  value: PartPrice;
  currency: ISO4217;
  transactionId: string;
}

// Used in ManageProduct and CreateNewProductWizard events
export interface ConfigurationEventData {
  account: string;
  user: string;
  role: string;
  productName: string;
  configurationPID: string;
}

export namespace GA {
  export function gtag(command: any, ...args: any) {
    /* istanbul ignore next */
    if (window.gtag) {
      window.gtag(command, ...args);
    }
  }

  export function dataLayerEvents(): UnstructuredEvent[] {
    /* istanbul ignore next */
    if (window.dataLayer) {
      return window.dataLayer.filter((item: StructuredEvent) => !item.event);
    }

    return [];
  }

  export namespace Ecommerce {
    export function viewItemList(listName: string, listId: string, currentParts: PartNode[]) {
      const eventData = {
        item_list_id: listId,
        item_list_name: listName,
        items: mapPartNodesToItems(currentParts),
        traffic_type: 'internal',
      };

      GA.gtag('event', 'view_item_list', eventData);
    }

    export function viewItem(tenant: AccountDBSource, currentParts: PartNode[], quantity: number) {
      const eventData = constructItemEvent(tenant, currentParts, quantity);
      GA.gtag('event', 'view_item', eventData);
    }

    export function selectItem(tenant: AccountDBSource, currentParts: PartNode[], quantity: number) {
      const eventData = constructItemEvent(tenant, currentParts, quantity);
      GA.gtag('event', 'select_item', eventData);
    }

    /* istanbul ignore next */
    export function addItemToCart(tenant: AccountDBSource, currentParts: PartNode[], quantity: number = 0) {
      const eventData = constructItemEvent(tenant, currentParts, quantity);
      GA.gtag('event', 'add_to_cart', eventData);
    }

    /* istanbul ignore next */
    export function removeItemFromCart(tenant: AccountDBSource, currentParts: PartNode[], quantity: number = 0) {
      const eventData = constructItemEvent(tenant, currentParts, quantity);
      GA.gtag('event', 'remove_from_cart', eventData);
    }

    export function beginCheckout(
      { name, preferredCurrency }: AccountDBSource,
      currentCartEntries: CartEntry[],
      cartTotal: DiscountedPartPrice
    ) {
      if (cartTotal === 'por') {
        return;
      }

      const items = mapCartEntriesToItems(currentCartEntries, name);
      const eventData = {
        currency: preferredCurrency ?? 'GBP',
        value: cartTotal,
        items,
      };

      GA.gtag('event', 'begin_checkout', eventData);
    }

    export function purchaseOrder(tenant: AccountDBSource, { items, value, currency, transactionId }: GTMDataPurchase) {
      if (
        GA.dataLayerEvents().filter(([, type, opts]) => type === 'purchase' && opts.transaction_id === transactionId)
          .length
      ) {
        return;
      }

      const eventData = {
        transaction_id: transactionId,
        value: value === 'por' ? 0 : value ?? /* istanbul ignore next */ 0,
        currency,
        items: items.map((item, index) => ({
          item_id: item.partNumber,
          item_name: item.partName,
          index,
          item_brand: tenant.name,
          price: item.unitPrice === 'por' ? 0 : item.unitPrice ?? /* istanbul ignore next */ 0,
          quantity: item.quantity,
          traffic_type: 'internal',
        })),
      };
      GA.gtag('event', 'purchase', eventData);
    }
  }

  export namespace ManageProductPage {
    export function downloadExampleKitTemplate(tenant: AccountDBSource, configurationPID: string, productName: string) {
      const eventData = { tenant, configurationPID, productName };
      GA.gtag('event', 'download_kits_template', eventData);
    }

    export function downloadExistingKitsFile(tenant: AccountDBSource, configurationPID: string, productName: string) {
      const eventData = { tenant, configurationPID, productName };
      GA.gtag('event', 'download_existing_kits_file', eventData);
    }

    export function uploadKitsSpreadsheet(tenant: AccountDBSource, configurationPID: string, productName: string) {
      const eventData = { tenant, configurationPID, productName };
      GA.gtag('event', 'upload_kits_spreadsheet', eventData);
    }

    export function learnMoreKits(tenant: AccountDBSource, configurationPID: string, productName: string) {
      const eventData = { tenant, configurationPID, productName };
      GA.gtag('event', 'manage_product_kits_knowledgebase', eventData);
    }

    export function greyscaleEnabled(
      tenant: AccountDBSource,
      productName: string,
      greyscaleEnabled: ColourMode.GREYSCALE
    ) {
      const eventData = { tenant, productName, greyscaleEnabled };
      GA.gtag('event', 'greyscale_enabled', eventData);
    }

    export function colourModeEnabled(
      tenant: AccountDBSource,
      productName: string,
      colourModeEnabled: ColourMode.COLOUR
    ) {
      const eventData = { tenant, productName, colourModeEnabled };
      GA.gtag('event', 'colour_mode_enabled', eventData);
    }

    export function productStatusDraftToLive(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_status_draft_to_live', eventData);
    }

    export function productStatusLiveToDraft(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_status_live_to_draft', eventData);
    }

    export function landedProductOverview(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_product_overview_visits', eventData);
    }

    export function landedSalesBOMMatchReport(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_match_report_visits', eventData);
    }

    export function learnMoreSalesBOM(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_matching_knowledgebase', eventData);
    }

    export function matchReportDownload(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_match_report_downloads', eventData);
    }

    /* istanbul ignore next - this is a temp message while MR gets re-generated */
    export function matchReportMissing(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_match_report_missing', eventData);
    }

    export function salesBOMUpload(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_match_report_uploads', eventData);
    }

    export function uploadedCustomThumbnail(eventData: ConfigurationEventData) {
      GA.gtag('event', 'manage_product_uploaded_custom_thumbnail', eventData);
    }
  }

  export namespace CreateNewProductWizard {
    export function wizardCompleted(eventData: ConfigurationEventData) {
      GA.gtag('event', 'new_product_wizard_completed', eventData);
    }
  }
}
